import { useEffect, useRef } from 'react'
import { useRouteError, useNavigate } from 'react-router-dom'
import { Button, Result } from 'antd'

interface RouteError {
  statusText?: string
  message?: string
  stack?: string
}

const isChunkLoadError = (error: RouteError): boolean => {
  const msg = error?.message || error?.statusText || ''
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('ERR_EMPTY_RESPONSE')
  )
}

export function ErrorPage() {
  const error = useRouteError() as RouteError
  const navigate = useNavigate()
  const hasAutoReloaded = useRef(false)

  const isChunkError = isChunkLoadError(error)

  useEffect(() => {
    if (hasAutoReloaded.current) return
    if (isChunkError) {
      hasAutoReloaded.current = true
      const currentPath = window.location.pathname + window.location.search
      window.location.replace(currentPath)
    }
  }, [isChunkError])

  console.error('🚨 [ROUTE_ERROR] 路由错误:', error)

  // chunk 加载错误时静默重试，只显示加载中，不展示错误信息
  if (isChunkError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '1.2rem', color: '#3b82f6' }}>
          加载中...
        </div>
      </div>
    )
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    navigate('/', { replace: true })
  }

  const handleRetry = () => {
    navigate(-1)
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        padding: '2rem',
      }}>
        <Result
          status="error"
          title="页面加载失败"
          subTitle={
            <>
              <p>抱歉,页面加载时遇到了问题。这可能是由于以下原因:</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                <li>网络连接不稳定</li>
                <li>页面模块加载失败</li>
                <li>服务器响应异常</li>
              </ul>
            </>
          }
          extra={[
            <Button type="primary" key="refresh" onClick={handleRefresh}>
              刷新页面
            </Button>,
            <Button key="retry" onClick={handleRetry}>
              返回上页
            </Button>,
            <Button key="home" onClick={handleGoHome}>
              返回首页
            </Button>,
          ]}
        >
          {error && (
            <details style={{
              marginTop: '1rem',
              textAlign: 'left',
              backgroundColor: '#fafafa',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid #d9d9d9'
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                查看错误详情
              </summary>
              <pre style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.875rem',
                color: '#666',
                margin: 0,
                marginTop: '0.5rem'
              }}>
                {error.message || error.statusText || '未知错误'}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </Result>
      </div>
    </div>
  )
}
